import {compact} from 'lodash/fp';
import {useMemo} from 'react';

import {FilterDialogSectionId} from 'filters/features/FilterDialog/types';
import {FilterTopbarItem, FilterTopbarItemId} from 'filters/features/FilterTopbar/types';
import {assertPolicyFilter, isPolicyFilter} from 'filters/types';

import {FeatureFlags} from 'core/constants/featureFlags';
import {useFeatureFlag} from 'core/hooks/authHooks';
import {usePolicyTagTreeViewSelection} from 'features/GlobalFilterProvider/filters/policyTags/usePolicyTagTreeViewSelection';
import {openFilterDialog} from 'filters';
import {createTreeViewSection} from 'filters/features/FilterDialog/sections/TreeViewSection/TreeViewSection';
import {createCounterItem} from 'filters/features/FilterTopbar/items/CounterItem/CounterItem';
import {usePolicyAgencyCodesTree} from 'modules/decisionsNext/hooks/trees/usePolicyAgencyCodesTree';

import PoliciesIcon from 'components/icons/metrics/policies.svg';

export const usePolicyTagsDialogSection = () => {
  const withPolicyTags = useFeatureFlag(FeatureFlags.PolicyTagsFilter);
  const withPolicySourceCodes = useFeatureFlag(FeatureFlags.PolicySourceCodes);

  return useMemo(
    () =>
      withPolicyTags &&
      createTreeViewSection({
        id: FilterDialogSectionId.PolicyTags,
        title: 'Policy Tags',
        icon: PoliciesIcon,
        useTreeData: usePolicyAgencyCodesTree,
        getDataOptions: value => ({
          skip: !value.codes?.length && !value.sources?.sources?.length,
        }),
        useTreeSelection: usePolicyTagTreeViewSelection,
        selectionOptions: {
          withPolicySourceCodes,
        },
        getValue: filter => {
          assertPolicyFilter(filter);

          return {
            codes: filter.policyAgencyCodes ?? null,
            sources: filter.policySourcesFilter
              ? {
                  isEmpty: filter.policySourcesFilter.isEmpty ?? undefined,
                  sources: filter.policySourcesFilter.sources ?? null,
                }
              : undefined,
          };
        },
        onChange: (filter, value) => {
          assertPolicyFilter(filter);

          return {
            ...filter,
            policyAgencyCodes: value.codes,
            policySourcesFilter: value.sources,
          };
        },
      }),
    [withPolicySourceCodes, withPolicyTags]
  );
};

export const usePolicyTagsTopbarItems = (): FilterTopbarItem[] => {
  const withPolicyTags = useFeatureFlag(FeatureFlags.PolicyTagsFilter);
  const withPolicySourceCodes = useFeatureFlag(FeatureFlags.PolicySourceCodes);

  return useMemo(() => {
    if (!withPolicyTags) {
      return [];
    }

    return compact([
      withPolicySourceCodes &&
        createCounterItem({
          id: FilterTopbarItemId.PolicySource,
          entity: 'Policy Source',
          getCount: filter => {
            if (!isPolicyFilter(filter)) {
              return;
            }

            return (filter.policySourcesFilter?.sources?.length || 0) + (filter.policySourcesFilter?.isEmpty ? 1 : 0);
          },
          onReset: filter => {
            assertPolicyFilter(filter);

            return {
              ...filter,
              policySourcesFilter: undefined,
            };
          },
          onClick: () => openFilterDialog(FilterDialogSectionId.PolicyTags),
        }),
      createCounterItem({
        id: FilterTopbarItemId.PolicyTag,
        entity: 'Policy Tags',
        getCount: filter => {
          if (!isPolicyFilter(filter)) {
            return;
          }

          return filter.policyAgencyCodes?.length;
        },
        onClick: () => openFilterDialog(FilterDialogSectionId.PolicyTags),
        onReset: filter => {
          assertPolicyFilter(filter);

          return {
            ...filter,
            policyAgencyCodes: undefined,
          };
        },
      }),
    ]);
  }, [withPolicySourceCodes, withPolicyTags]);
};
